import * as React from 'react';
import styled from 'styled-components';
import * as tokens from '@utmost.co/utmost-design-tokens';
import appTheme from '../../theme/appTheme';
import { CircularProgress, CircularProgressProps } from '../ProgressBar/CircularProgress';

const bounceHeight = 1;
const bounceHeightMultiplier = 1;

const StyledLoadingWrapper = styled.div`
  color: ${(e) => e.theme.colorPrimary};
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  position: relative;

  .bounce {
    margin: auto;
    position: absolute;
    top: -16px;
    left: 0;
    right: 0;
    animation: bounce 1s infinite;
    background-size: 100%;
    background-repeat: no-repeat;
  }

  @keyframes bounce {
    0% {
      transform: translateY(${bounceHeight * bounceHeightMultiplier}px);
    }
    5% {
      transform: translateY(${bounceHeight * 1.3 * bounceHeightMultiplier}px);
    }
    10% {
      transform: translateY(${bounceHeight * 2 * bounceHeightMultiplier}px);
    }
    15% {
      transform: translateY(${bounceHeight * 3.3 * bounceHeightMultiplier}px);
    }
    20% {
      transform: translateY(${bounceHeight * 6.3 * bounceHeightMultiplier}px);
    }
    25% {
      transform: translateY(${bounceHeight * 12 * bounceHeightMultiplier}px);
    }
    30% {
      transform: translateY(${bounceHeight * 16.6 * bounceHeightMultiplier}px);
    }
    35% {
      transform: translateY(${bounceHeight * 25.3 * bounceHeightMultiplier}px);
    }
    40% {
      transform: translateY(${bounceHeight * 25.6 * bounceHeightMultiplier}px) scale(1.1, 0.9);
    }
    50% {
      transform: translateY(${bounceHeight * 29.3 * bounceHeightMultiplier}px) scale(1.4, 0.6);
    }
    55% {
      transform: translateY(${bounceHeight * 27 * bounceHeightMultiplier}px) scale(1.2, 0.8);
    }
    60% {
      transform: translateY(${bounceHeight * 23 * bounceHeightMultiplier}px) scale(1.05, 0.95);
    }
    65% {
      transform: translateY(${bounceHeight * 18.3 * bounceHeightMultiplier}px);
    }
    70% {
      transform: translateY(${bounceHeight * 12 * bounceHeightMultiplier}pxpx);
    }
    75% {
      transform: translateY(${bounceHeight * 6.3 * bounceHeightMultiplier}px);
    }
    80% {
      transform: translateY(${bounceHeight * 3.3 * bounceHeightMultiplier}px);
    }
    85% {
      transform: translateY(${bounceHeight * 2 * bounceHeightMultiplier}px);
    }
    90% {
      transform: translateY(${bounceHeight * 1.3 * bounceHeightMultiplier}px);
    }
    95% {
      transform: translateY(${bounceHeight * 0.83 * bounceHeightMultiplier}px);
    }
    100% {
      transform: translateY(${bounceHeight * 0.83 * bounceHeightMultiplier}px);
    }
  }
`;

export interface LoadingProps {
  opacity?: number;
  theme?: typeof appTheme;
  outerStyle?: React.CSSProperties;
  className?: string;
  logo?: boolean;
  inline?: boolean;
  margin?: 'large' | 'small' | 'none';
  size?: 'small' | 'medium' | 'large' | 'xLarge';
  alignment?: 'center' | 'none';
}

const Loading: React.FC<React.PropsWithChildren<LoadingProps & CircularProgressProps>> = (props) => {
  const { opacity, inline, alignment, margin, size, logo, outerStyle, ...circularProps } = props;
  let loaderSize = 28;

  if (size === 'small') loaderSize = 18;
  else if (size === 'large') loaderSize = 40;
  else if (size === 'xLarge') loaderSize = 54;

  return (
    <StyledLoadingWrapper
      className="loading__component__wrapper"
      data-test="loading__component"
      style={{
        display: inline ? 'inline-flex' : 'flex',
        margin:
          alignment === 'center' ? `${margin === 'large' ? '3rem' : margin === 'none' ? '0' : '1rem'} auto` : undefined,
        width: `${loaderSize}px`,
        height: `${loaderSize}px`,
        ...outerStyle
      }}
    >
      {logo ? (
        <div
          className="circular__loading loading bounce"
          style={{ width: `${loaderSize}px`, height: `${loaderSize}px` }}
        />
      ) : (
        <CircularProgress
          className="circular__loading loading"
          style={{ opacity: props.opacity, color: tokens.colorPrimary }}
          {...(circularProps as any)}
          size={loaderSize}
        />
      )}
    </StyledLoadingWrapper>
  );
};

Loading.defaultProps = {
  opacity: 1,
  size: 'medium',
  alignment: 'center',
  margin: 'large'
};

export default Loading;
